import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import useTaskListTemplate, { useTaskListTemplateForm } from '@hooks/useTaskListTemplate'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import OrganizationSelector from '@components/OrganizationSelector'

const MODAL_KEY = 'CreateOrEditTaskListTemplateModal'

type ModalCallbacks = {
  closeModal: () => void,
  createTaskListTemplate: (payload: HandleCallbackActionParams) => void,
  updateTaskListTemplate: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditTaskListTemplateModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createTaskListTemplate, updateTaskListTemplate } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] ?? {}
  const taskListTemplate = modalPayload?.taskListTemplate ?? {}

  const newRecord = !taskListTemplate.id

  const {
    callbacks: {
      createTaskListTemplate: createFn,
      updateTaskListTemplate: updateFn,
    },
    creating,
    updating,
  } = useTaskListTemplate(taskListTemplate)

  const {
    entityState,
    entityState: {
      owner_id,
      owner_type,
      template_checklist,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useTaskListTemplateForm(taskListTemplate, { validateOn: 'change' })

  // To be used with handleCallbackAction
  const createOrUpdateTaskListTemplatePayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Task List Template ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Task List Template"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Owner Type" errorMessage={errors.owner_type}>
            <select
              name="owner_type"
              value={owner_type}
              onChange={e => setEntityState({
                owner_id: undefined,
                owner_type: e.target.value,
                template_checklist: true,
              })}
            >
              <option value="">Please Select...</option>
              <option value="Agency">Agency</option>
              <option value="Brand">Brand</option>
              <option value="Provider">Provider</option>
              <option value="System">System</option>
            </select>
          </Form.Field>

          {!!owner_type && (
            <OrganizationSelector
              callbacks={{
                // @ts-expect-error TS2339
                selectItem: ({ entity }) => setEntityState({ owner_id: entity?.id }),
              }}
              entityType={owner_type}
              label="* Owner"
              marginTop="large"
              selectedItemId={owner_id}
            />
          )}

          <Form.Field label="* Title" errorMessage={errors.title} marginTop="large">
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Template Checklist" errorMessage={errors.template_checklist} marginTop="large">
            <select itemType="boolean" name="template_checklist" value={template_checklist}>
              <option value="">Please Select...</option>
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createTaskListTemplate(createOrUpdateTaskListTemplatePayload)
              : () => updateTaskListTemplate(createOrUpdateTaskListTemplatePayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Task List Template` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditTaskListTemplateModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
